import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Eye, EyeOff, CheckCircle2, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const { t } = useTranslation(["auth", "common"]);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  
  useEffect(() => {
    if (!token) {
      setTokenError("Geçersiz bağlantı");
      setVerifying(false);
      return;
    }
    (async () => {
      try {
        const r = await fetch(`${API}/auth/password-reset/verify`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        });
        if (r.ok) {
          const data = await r.json();
          setTokenValid(true);
          if (data.email) {
            const [u, d] = data.email.split("@");
            setMaskedEmail(`${u.slice(0, 2)}***@${d}`);
          }
        } else {
          const err = await r.json().catch(() => ({}));
          setTokenError(err.detail || "Bağlantı geçersiz veya süresi dolmuş");
        }
      } catch {
        setTokenError("Bağlantı doğrulanamadı");
      } finally {
        setVerifying(false);
      }
    })();
  }, [token]);
  
  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Şifre en az 6 karakter olmalıdır");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Şifreler eşleşmiyor");
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/auth/password-reset/confirm`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: password }),
      });
      if (r.ok) {
        setDone(true);
        toast.success("Şifreniz güncellendi");
        setTimeout(() => navigate("/auth?mode=login"), 2500);
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || "Bir hata oluştu");
      }
    } catch {
      toast.error("Bağlantı hatası");
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "linear-gradient(135deg, #0F1F35 0%, #1e3a5f 100%)" }}>
      <Card className="w-full max-w-md border-0 shadow-2xl">
        <CardContent className="p-8">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-semibold text-navy">Şifremi Sıfırla</h1>
            <p className="text-sm text-gray-600 mt-1">PBStudio hesabınız için yeni şifrenizi belirleyin</p>
          </div>
          
          {verifying ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy mx-auto mb-3" />
              <p className="text-sm text-gray-600">Bağlantı doğrulanıyor...</p>
            </div>
          ) : !tokenValid ? (
            <div className="text-center py-6 space-y-4">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto" />
              <p className="text-sm text-gray-700">{tokenError}</p>
              <Link to="/auth?mode=login">
                <Button className="bg-navy hover:bg-navy/90 rounded-xl" data-testid="reset-back-login">
                  Giriş Sayfasına Dön
                </Button>
              </Link>
            </div>
          ) : done ? (
            <div className="text-center py-6 space-y-4">
              <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto" />
              <p className="text-sm text-gray-700">
                Şifreniz başarıyla güncellendi! Giriş sayfasına yönlendiriliyorsunuz...
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              {maskedEmail && (
                <div className="bg-slate-50 rounded-xl p-3 text-sm text-gray-700">
                  <strong>Hesap:</strong> {maskedEmail}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Yeni Şifre</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="En az 6 karakter"
                    className="w-full px-4 py-3 pr-12 rounded-xl border border-gray-300 focus:border-navy focus:ring-2 focus:ring-navy/20 outline-none"
                    required
                    minLength={6}
                    data-testid="reset-new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Şifre (Tekrar)</label>
                <input
                  type={showPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Yeni şifrenizi tekrar girin"
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-navy focus:ring-2 focus:ring-navy/20 outline-none"
                  required
                  minLength={6}
                  data-testid="reset-confirm-password"
                />
              </div>
              <Button
                type="submit"
                className="w-full h-12 bg-navy hover:bg-navy/90 rounded-xl font-semibold"
                disabled={submitting}
                data-testid="reset-submit"
              >
                {submitting ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                ) : (
                  "Şifremi Güncelle"
                )}
              </Button>
              <Link to="/auth?mode=login" className="block text-center text-sm text-navy hover:underline">
                Giriş sayfasına dön
              </Link>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
